import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const validateAndSanitizeSocialLink = (url) => {
  if (!url || typeof url !== 'string') throw new Error('URL tidak valid');
  let sanitizedUrl = url.trim();
  try {
    const urlObj = new URL(sanitizedUrl);
    sanitizedUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
  } catch { throw new Error('Format URL tidak valid'); }
  return sanitizedUrl.replace(/\/+$/, '');
};

const PLATFORMS = ['Instagram', 'Tiktok', 'Youtube'];
const PLACEHOLDERS = {
  Instagram: 'https://instagram.com/p/...',
  Tiktok: 'https://www.tiktok.com/@username/video/...',
  Youtube: 'https://www.youtube.com/watch?v=...',
};

const CreateProposal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns]                   = useState([]);
  const [campaignId, setCampaignId]                 = useState('');
  const [socialMediaLink, setSocialMediaLink]       = useState('');
  const [socialMediaPlatform, setSocialMediaPlatform] = useState('');
  const [loading, setLoading]                       = useState(false);
  const [loadingCampaigns, setLoadingCampaigns]     = useState(true);
  const [error, setError]                           = useState('');

  useEffect(() => { loadCampaigns(); }, []); // eslint-disable-line

  const loadCampaigns = async () => {
    try {
      const data = await api.get('/api/campaigns');
      const available = data.filter(c => c.is_registered && c.status !== 'stopped' && !c.is_quota_full);
      setCampaigns(available);
      const paramId = searchParams.get('campaign_id');
      if (paramId && available.some(c => c.id === paramId)) setCampaignId(paramId);
      else if (available.length > 0) setCampaignId(available[0].id);
    } catch { /* silent */ }
    finally { setLoadingCampaigns(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!socialMediaPlatform) { setError('Pilih platform terlebih dahulu'); return; }
    let validatedLink;
    try { validatedLink = validateAndSanitizeSocialLink(socialMediaLink); setSocialMediaLink(validatedLink); }
    catch (err) { setError(err.message); return; }
    setLoading(true);
    try {
      await api.post('/api/proposals', { campaign_id: campaignId, social_media_link: validatedLink, social_media_platform: socialMediaPlatform });
      navigate(`/campaigns/${campaignId}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Terjadi kesalahan saat submit post link');
    } finally { setLoading(false); }
  };

  if (loadingCampaigns) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-lg space-y-4">
      <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Submit Post Link</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 space-y-5" style={{ boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' }}>
        {error && (
          <div className="bg-red-50 border border-red-200 text-[13px] text-[#c62828] px-4 py-3 rounded-xl">{error}</div>
        )}

        {/* Campaign select */}
        <div>
          <label className={LABEL}>Campaign *</label>
          {campaigns.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-[13px] font-semibold text-amber-700">Tidak ada campaign tersedia.</p>
              <p className="text-[12px] text-amber-600 mt-0.5">Daftar ke campaign aktif di halaman Campaigns, atau tunggu slot kuota tersedia.</p>
            </div>
          ) : (
            <select required value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={INPUT}>
              <option value="">Pilih campaign</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>

        {/* Platform select */}
        <div>
          <label className={LABEL}>Platform *</label>
          <select required value={socialMediaPlatform} onChange={(e) => setSocialMediaPlatform(e.target.value)} className={INPUT}>
            <option value="">Pilih Platform</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Link input */}
        <div>
          <label className={LABEL}>Social Media Post Link *</label>
          <input type="url" required value={socialMediaLink} onChange={(e) => setSocialMediaLink(e.target.value)}
            className={INPUT} placeholder={PLACEHOLDERS[socialMediaPlatform] || 'https://...'} />
          <p className="text-[11px] text-[#8a8a8a] mt-1">Masukkan link post social media Anda.</p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate('/campaigns')}
            className="px-4 py-2 text-[13px] font-medium text-[#646464] border border-[#e5e5e5] rounded-lg hover:bg-gray-50 transition-colors">
            Batal
          </button>
          <button type="submit" disabled={loading || !campaignId || !socialMediaPlatform || !socialMediaLink}
            className="px-5 py-2 text-[13px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white rounded-lg transition-colors disabled:opacity-50">
            {loading ? 'Submitting...' : 'Submit Post Link'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProposal;
